import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { useCart } from "../../context/CartContext";

const cats = [
  { id: "all", label: "All" },
  { id: "shirt", label: "Shirts" },
  { id: "hat", label: "Hats" },
  { id: "bundle", label: "Bundles" },
];

const COLOR_HEX = {
  Black: "#111111",
  "Jet Black": "#0a0a0a",
  "Washed Black": "#1c1c1c",
  Charcoal: "#2f3033",
  "Heather Gray": "#8a8d91",
  Gray: "#8a8d91",
  Bone: "#e8e2d0",
  Navy: "#1e2a44",
  "Midnight Navy": "#141e33",
  "Forest Green": "#2f4a37",
  "Army Green": "#4a5133",
  Blue: "#2f56a6",
  "Royal Blue": "#22458c",
  Olive: "#4b5320",
  "Brick Red": "#8a2f2a",
  Oxblood: "#4a0e17",
  "Coyote Tan": "#8a6a44",
  Digicam: "#4d5638",
  "Black/White": "#1a1a1a",
  "Gray/Black": "#6b6e72",
  "Navy/White": "#28324f",
  "Team Issue": "#2f4a37",
};

const swatch = (c) => COLOR_HEX[c] || "#555";

const AddDialog = ({ product, initialColorIdx = 0, onClose }) => {
  const { addItem } = useCart();
  const [size, setSize] = useState(product.variants.sizes[0]);
  const cvs = product.color_variants || [];
  const [colorIdx, setColorIdx] = useState(cvs.length > 0 ? initialColorIdx : 0);
  const [color, setColor] = useState(product.variants.colors[0]);

  // If color_variants exist, the swatch drives everything.
  const activeVariant = cvs[colorIdx];
  const heroImage = activeVariant ? activeVariant.image : product.image;
  const heroColorLabel = activeVariant ? activeVariant.label : color;

  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#FAF9F5] border-2 border-[#111111] text-[#111111] max-w-3xl p-0 rounded-sm max-h-[90vh] overflow-hidden"
        data-testid="product-dialog"
      >
        <div className="overflow-y-auto max-h-[90vh]">
        <div className="grid md:grid-cols-2">
          <div className="relative bg-[#111] aspect-square overflow-hidden flex items-center justify-center">
            <img
              src={heroImage}
              alt={`${product.name} — ${heroColorLabel}`}
              data-testid="dialog-hero-image"
              className="w-full h-full object-contain transition-opacity duration-300"
              key={heroImage}
            />
            {product.bestseller && (
              <span className="absolute top-4 left-4 bg-[#FF4500] text-white text-xs font-black px-3 py-1 rounded-sm flex items-center gap-1 uppercase tracking-wider">
                <Star className="h-3 w-3 fill-white" /> Best Seller
              </span>
            )}
          </div>
          <div className="p-6 md:p-8">
            <DialogHeader>
              <DialogTitle className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl text-left">
                {product.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Select size and color for {product.name} and add it to your cart.
              </DialogDescription>
            </DialogHeader>
            <p className="text-[#FF4500] text-sm font-medium mt-1">{product.tagline}</p>
            <p className="text-[#555555] text-sm mt-4 leading-relaxed">{product.description}</p>

            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-widest text-[#555555] mb-2">Size</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`px-3 py-2 text-xs font-bold uppercase rounded-sm border transition-colors duration-200 ${
                      size === s
                        ? "bg-[#FF4500] text-white border-[#FF4500]"
                        : "border-[#111111]/20 text-[#333333] hover:border-[#111111]/60"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-widest text-[#555555] mb-2">
                Color{cvs.length > 0 ? <span className="ml-2 text-[#111111] font-bold normal-case tracking-normal">{heroColorLabel}</span> : null}
              </p>
              {cvs.length > 0 ? (
                <div className="flex flex-wrap items-center gap-3" data-testid="dialog-color-swatches">
                  {cvs.map((c, i) => (
                    <button
                      key={c.key}
                      onClick={() => setColorIdx(i)}
                      title={c.label}
                      data-testid={`dialog-swatch-${c.key}`}
                      className={`h-7 w-7 rounded-full border-2 transition-all duration-200 ${
                        i === colorIdx
                          ? "border-[#FF4500] ring-2 ring-[#FF4500]/40 scale-110"
                          : "border-[#111111]/25 hover:border-[#111111]/60"
                      }`}
                      style={{ backgroundColor: c.hex }}
                      aria-label={`Select ${c.label} colorway`}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {product.variants.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      data-testid={`color-${c}`}
                      className={`px-3 py-2 text-xs font-bold uppercase rounded-sm border transition-colors duration-200 ${
                        color === c
                          ? "bg-[#111111] text-white border-[#111111]"
                          : "border-[#111111]/20 text-[#333333] hover:border-[#111111]/60"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <span className="font-display font-black text-3xl">${product.price.toFixed(0)}</span>
              <button
                onClick={() => {
                  if (cvs.length > 0) {
                    addItem(product, size, activeVariant.label, 1, {
                      image: activeVariant.image,
                      color_key: activeVariant.key,
                    });
                  } else {
                    addItem(product, size, color, 1);
                  }
                  onClose();
                }}
                data-testid="dialog-add-to-cart"
                className="bg-[#FF4500] text-white px-6 py-3.5 rounded-sm font-black uppercase text-sm tracking-wider hover:bg-[#111111] transition-colors duration-200"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProductCard = ({ product, index, onSelect }) => {
  // Which colorway is currently displayed. Defaults to the product's canonical image.
  const cvs = product.color_variants || [];
  const currentIdx = cvs.findIndex((c) => c.image === product.image);
  const [activeIdx, setActiveIdx] = useState(currentIdx >= 0 ? currentIdx : 0);
  const activeVariant = cvs[activeIdx];
  const displayImage = activeVariant ? activeVariant.image : product.image;

  return (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative bg-[#FAF9F5] border border-[#111111]/15 rounded-sm overflow-hidden hover:border-[#111111] hover:-translate-y-1 hover:shadow-[6px_6px_0px_#111111] transition-all duration-300 cursor-pointer ${
      product.featured ? "lg:row-span-1" : ""
    }`}
    data-testid={`product-card-${product.slug}`}
    onClick={() => onSelect(product, activeIdx)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(product, activeIdx);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="relative aspect-[4/5] overflow-hidden bg-[#141414]">
      <img
        src={displayImage}
        alt={product.name}
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
          product.image_lifestyle && activeIdx === currentIdx ? "group-hover:opacity-0" : ""
        }`}
      />
      {product.image_lifestyle && activeIdx === currentIdx && (
        <img
          src={product.image_lifestyle}
          alt={`${product.name} — front and back`}
          data-testid={`lifestyle-${product.slug}`}
          className="absolute inset-0 w-full h-full object-contain bg-[#141414] opacity-0 scale-105 transition-all duration-500 group-hover:opacity-100 group-hover:scale-100"
        />
      )}
      {product.bestseller && (
        <span
          data-testid={`bestseller-${product.slug}`}
          className="absolute top-4 left-4 bg-[#FF4500] text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-sm flex items-center gap-1"
        >
          <Star className="h-3 w-3 fill-white" /> Best Seller
        </span>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect(product, activeIdx);
        }}
        data-testid={`quick-add-${product.slug}`}
        aria-label={`Add ${product.name}`}
        className="absolute bottom-4 right-4 h-11 w-11 bg-[#FAF9F5]/90 backdrop-blur-md border border-[#111111]/20 rounded-sm flex items-center justify-center text-[#111111] opacity-100 md:opacity-0 md:translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 hover:bg-[#FF4500] hover:text-white hover:border-[#FF4500] transition-all duration-300"
      >
        <Plus className="h-5 w-5" />
      </button>
    </div>
    <div className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-bold uppercase tracking-tight text-lg leading-tight text-[#111111]">
            {product.name}
          </h3>
          <p className="text-xs text-[#555555] mt-1 uppercase tracking-wider">{product.tagline}</p>
          {cvs.length > 0 ? (
            <div className="flex items-center gap-2 mt-3" data-testid={`swatches-${product.slug}`}>
              {cvs.map((c, i) => (
                <button
                  key={c.key}
                  title={c.label}
                  data-testid={`swatch-${product.slug}-${c.key}`}
                  onClick={(e) => { e.stopPropagation(); setActiveIdx(i); }}
                  aria-label={`View ${product.name} in ${c.label}`}
                  className={`h-4 w-4 rounded-full border transition-all duration-200 ${
                    i === activeIdx
                      ? "border-[#FF4500] ring-1 ring-[#FF4500] scale-110"
                      : "border-[#111111]/30 hover:border-[#111111]/70"
                  }`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          ) : product.variants?.colors?.length > 0 ? (
            <div className="flex items-center gap-1.5 mt-2.5" data-testid={`swatches-${product.slug}`}>
              {product.variants.colors.map((c) => (
                <span
                  key={c}
                  title={c}
                  className="h-3.5 w-3.5 rounded-full border border-[#111111]/25"
                  style={{ backgroundColor: swatch(c) }}
                />
              ))}
            </div>
          ) : null}
        </div>
        <span className="font-display font-black text-xl shrink-0 text-[#111111]">${product.price.toFixed(0)}</span>
      </div>
    </div>
  </motion.div>
  );
};

export const Store = ({ products }) => {
  const [cat, setCat] = useState("all");
  const [selected, setSelected] = useState(null);
  const filtered = cat === "all" ? products : products.filter((p) => p.category === cat);

  return (
    <section id="store" className="relative py-24 md:py-36" data-testid="store-section">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF4500] mb-4">
              Official Merch
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.85] text-[#111111]">
              Shop the Drop
            </h2>
            <p className="text-[#555555] mt-4 max-w-md leading-relaxed">
              Explore our full line of t-shirts, snapbacks, and riding gear.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="store-filters">
            {cats.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`filter-${c.id}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors duration-200 ${
                  cat === c.id
                    ? "bg-[#FF4500] text-white border-[#FF4500]"
                    : "border-[#111111]/20 text-[#333333] hover:border-[#111111]/60"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              index={i}
              onSelect={(prod, idx) => setSelected({ product: prod, initialColorIdx: idx || 0 })}
            />
          ))}
        </div>
      </div>

      {selected && (
        <AddDialog
          product={selected.product}
          initialColorIdx={selected.initialColorIdx}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
};

export default Store;
